import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/** Custom dashboards — mirrors server/boards/. (API prefix /api/boards; /api/dashboards is PSA's.) */

export type MetricModule = 'psa' | 'rmm' | 'cipp' | 'backup' | 'network' | 'domains' | 'docs' | 'insights' | 'csp' | 'soc';
export type MetricKind = 'number' | 'breakdown' | 'series' | 'table';
export type Tone = 'success' | 'warning' | 'serious' | 'destructive' | 'info' | 'neutral' | 'subtle';

export interface MetricDef {
  id: string;
  module: MetricModule;
  label: string;
  description: string;
  kind: MetricKind;
  unit?: 'count' | 'percent' | 'currency' | 'score';
  better: 'up' | 'down' | null;
  scoped: boolean;
  psaScope?: boolean;
  series?: { key: string; label: string; unit?: 'percent' | 'count' }[];
}

export type MetricValue =
  | { kind: 'number'; value: number | null; total?: number | null }
  | { kind: 'breakdown'; items: { label: string; value: number; tone?: Tone }[] }
  | { kind: 'series'; points: Record<string, string | number | null>[] }
  | { kind: 'table'; rows: { label: string; value: number | string | null; sub?: string | null }[] };

export interface MetricResult { value: MetricValue | null; error: string | null; loading?: boolean; scopedApplied: boolean }

export type CardType = 'kpi' | 'bar' | 'donut' | 'line' | 'list' | 'note' | 'links' | 'clock';
export type Accent = 'brand' | 'success' | 'warning' | 'destructive' | 'info' | 'neutral';

export interface CardParams { companyId?: number | null; companyName?: string | null; psaScope?: 'me' | 'team' }

export interface Card {
  id: string;
  type: CardType;
  title?: string;
  metric?: string;
  params?: CardParams;
  options?: {
    thresholds?: { warn?: number | null; bad?: number | null };
    showTotal?: boolean;
    limit?: number;
    text?: string;
    links?: { label: string; url: string }[];
    accent?: Accent;
  };
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardDefinition {
  cards: Card[];
  refreshSeconds: number;
  /** picker = follow the header client picker; all = every client; fixed = always one client. */
  client: { mode: 'picker' | 'all' | 'fixed'; companyId?: number | null; companyName?: string | null };
}

export interface Access { canView: boolean; canEdit: boolean; isOwner: boolean }

export interface Dashboard {
  id: string;
  name: string;
  description: string | null;
  ownerId: string;
  ownerName: string;
  everyone: 'none' | 'view' | 'edit';
  definition: DashboardDefinition;
  version: number;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface DashboardListItem extends Omit<Dashboard, 'definition'> {
  cards: number;
  access: Access;
  favourite: boolean;
  isDefault: boolean;
  via: 'mine' | 'shared' | 'everyone';
}

export interface Template { id: string; name: string; description: string; cards: number }

export function useMetricCatalogue() {
  return useQuery({ queryKey: ['boards', 'metrics'], queryFn: () => api.get<{ metrics: MetricDef[] }>('/api/boards/metrics'), staleTime: Infinity });
}

export function useBoardTemplates(enabled = true) {
  return useQuery({ enabled, queryKey: ['boards', 'templates'], queryFn: () => api.get<{ templates: Template[] }>('/api/boards/templates'), staleTime: Infinity });
}

export function useBoards(enabled = true) {
  return useQuery({ queryKey: ['boards', 'list'], enabled, queryFn: () => api.get<{ dashboards: DashboardListItem[] }>('/api/boards'), staleTime: 30_000 });
}

export function useBoard(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['boards', 'one', id],
    queryFn: () => api.get<{ dashboard: Dashboard; access: Access }>(`/api/boards/${id}`),
    staleTime: 30_000,
  });
}

/**
 * One request for every metric card on a dashboard; refetches on the dashboard's interval.
 * `background`: keep polling while the tab is hidden — only the wall display (DashboardDisplay) wants that;
 * a dashboard open in a background tab waits until it's visible again.
 */
export function useBoardValues(items: { key: string; metric: string; params?: CardParams }[], refreshSeconds: number, enabled = true, { background = false }: { background?: boolean } = {}) {
  const body = items.map((i) => ({ key: i.key, metric: i.metric, params: i.params ?? {} }));
  return useQuery({
    enabled: enabled && body.length > 0,
    queryKey: ['boards', 'values', body],
    queryFn: () => api.post<{ values: Record<string, MetricResult>; at: string }>('/api/boards/metrics/values', { items: body }),
    refetchInterval: refreshSeconds * 1000,
    refetchIntervalInBackground: background,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return (id?: string) => {
    qc.invalidateQueries({ queryKey: ['boards', 'list'] });
    if (id) qc.invalidateQueries({ queryKey: ['boards', 'one', id] });
  };
}

export function useCreateBoard() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { name: string; template: string } | { name: string; description?: string | null; definition: DashboardDefinition }) =>
      api.post<{ dashboard: Dashboard }>('/api/boards', body),
    onSuccess: () => invalidate(),
  });
}

export function useSaveBoard() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (d: Pick<Dashboard, 'id' | 'name' | 'description' | 'definition' | 'version'>) =>
      api.put<{ dashboard: Dashboard; access: Access }>(`/api/boards/${d.id}`, { name: d.name, description: d.description, definition: d.definition, version: d.version }),
    onSuccess: (r) => { qc.setQueryData(['boards', 'one', r.dashboard.id], r); invalidate(); },
  });
}

export function useDeleteBoard() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: string) => api.del<{ ok: true }>(`/api/boards/${id}`), onSuccess: () => invalidate() });
}

export function useDuplicateBoard() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: ({ id, name }: { id: string; name?: string }) => api.post<{ dashboard: Dashboard }>(`/api/boards/${id}/duplicate`, { name }), onSuccess: () => invalidate() });
}

export function useFavouriteBoard() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, on, isDefault }: { id: string; on: boolean; isDefault?: boolean }) => api.put<{ ok: true }>(`/api/boards/${id}/favourite`, { on, isDefault }),
    onSuccess: () => invalidate(),
  });
}

export interface BoardShares { everyone: 'none' | 'view' | 'edit'; users: { userId: string; permission: 'view' | 'edit'; name: string | null; email: string | null }[] }

export function useBoardShares(id: string, enabled: boolean) {
  return useQuery({ enabled, queryKey: ['boards', 'shares', id], queryFn: () => api.get<BoardShares>(`/api/boards/${id}/shares`) });
}

export function useSaveBoardShares() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; everyone: BoardShares['everyone']; users: { userId: string; permission: 'view' | 'edit' }[] }) =>
      api.put<BoardShares>(`/api/boards/${id}/shares`, body),
    onSuccess: (r, v) => { qc.setQueryData(['boards', 'shares', v.id], r); invalidate(v.id); },
  });
}

export function useCosmosUsers(enabled: boolean) {
  return useQuery({ enabled, queryKey: ['boards', 'users'], queryFn: () => api.get<{ users: { id: string; name: string; email: string }[] }>('/api/boards/users'), staleTime: 5 * 60_000 });
}
